import { ErrorResponse } from 'oidc-client-ts';
import { ximiUserManager } from './oidcConfig';

/** Header transportant la preuve SSO par requête vers StimulinAPI (users Ximi). */
export const XIMI_SSO_HEADER = 'X-Ximi-Sso-Token';

/** Access token SSO Xelya courant (null si aucune session ou session expirée). */
export async function getCurrentXimiSsoAccessToken(): Promise<string | null> {
    const user = await ximiUserManager.getUser();
    if (!user || user.expired) return null;
    return user.access_token ?? null;
}

/**
 * Le renouvellement SSO n'a pas pu être tenté jusqu'au bout (réseau, AuthProvider injoignable,
 * timeout) : la session SSO n'est PAS morte, il faut réessayer plus tard — jamais déconnecter.
 */
export class XimiSsoUnavailableError extends Error {
    constructor(cause: unknown) {
        super(`Ximi SSO unavailable: ${cause instanceof Error ? cause.message : String(cause)}`);
        this.name = 'XimiSsoUnavailableError';
    }
}

// Renew partagé : N requêtes simultanées sur un token expiré = UNE seule tentative signinSilent.
let renewInFlight: Promise<string | null> | null = null;

/**
 * Forced SSO renew (SSO refresh token), even if the current token has not expired: used when a
 * server has just rejected that token. Same outcomes as `ensureFreshXimiSsoAccessToken`.
 */
export async function renewXimiSsoAccessToken(): Promise<string | null> {
    const user = await ximiUserManager.getUser();
    if (!user) return null; // pas de session SSO (user non-Ximi) — rien à renouveler

    if (!renewInFlight) {
        renewInFlight = ximiUserManager
            .signinSilent()
            .then((renewed) => renewed?.access_token ?? null)
            .catch((error: unknown) => {
                if (error instanceof ErrorResponse) return null; // refus explicite du SSO
                throw new XimiSsoUnavailableError(error);
            })
            .finally(() => {
                renewInFlight = null;
            });
    }
    return renewInFlight;
}

/**
 * Token SSO frais : celui de la session s'il est valide, sinon une tentative de
 * silent renew (refresh token SSO). null = session SSO absente ou définitivement morte
 * (réponse d'erreur OIDC, ex. refresh token SSO expiré ou compte désactivé).
 * Lève `XimiSsoUnavailableError` si AuthProvider n'a pas pu répondre.
 */
export async function ensureFreshXimiSsoAccessToken(): Promise<string | null> {
    const current = await getCurrentXimiSsoAccessToken();
    if (current) return current;
    return renewXimiSsoAccessToken();
}

/** Purge locale de la session SSO morte (sans end_session : elle est déjà invalide côté SSO). */
export async function clearXimiSsoSession(): Promise<void> {
    try {
        await ximiUserManager.removeUser();
    } catch {
        // purge best-effort — ne doit jamais bloquer le logout
    }
}
